// The farm's seasonal pump program — explicit ON cycles per system, per season.
//
// Each schedule is a list of ON cycles `{ hour, startMin, on }` where `on` is the
// run length in minutes. `hour` is 0–23 so overnight maintenance pulses (10 PM,
// 2 AM, …) are representable — the earlier pattern generator could only place
// cycles inside the daytime operating window, which this program needs to break.
//
// This is the authoritative program supplied by the grower. The custom-schedule
// editor lets a user replace any system/season list with their own; these are the
// defaults and what "Reset to default" restores.
//
// Runtimes are asserted by src/__tests__/pumpSchedules.test.js against the
// grower's matrix (NFT 6h30/7h15/8h, Tower 4h15/6h/7h30, Nursery 20/30/40 min).

/** Build regular hourly cycles: for each hour in [startHour, endHour), one ON
 *  cycle at each minute offset. Used for the even NFT and hot-tower cadences. */
export function genHourlyCycles(startHour, endHour, onMin, minuteOffsets) {
  const cycles = [];
  for (let h = startHour; h < endHour; h++) {
    for (const m of minuteOffsets) cycles.push({ hour: h, startMin: m, on: onMin });
  }
  return cycles;
}

const c = (hour, startMin, on) => ({ hour, startMin, on });

export const DEFAULT_PUMP_SCHEDULES = {
  // ── COOL ────────────────────────────────────────────────────────────────
  cool: {
    // 30 ON / 30 OFF, 6 AM–6 PM (12 runs) + two 15-min night pulses.
    nft: [
      ...genHourlyCycles(6, 18, 30, [0]),
      c(22, 0, 15), c(2, 0, 15),
    ],
    // 15-min runs; morning hourly, tightening midday, then hourly evening.
    tower: [
      c(6, 0, 15), c(7, 0, 15), c(8, 0, 15), c(9, 0, 15),
      c(10, 0, 15), c(10, 45, 15), c(11, 30, 15), c(12, 15, 15),
      c(13, 0, 15), c(13, 45, 15), c(14, 30, 15), c(15, 15, 15),
      c(16, 0, 15), c(17, 0, 15), c(18, 0, 15),
      c(22, 15, 15), c(2, 30, 15),
    ],
    // 5-min spray pulses, every ~4 hours.
    nursery: [c(6, 30, 5), c(10, 30, 5), c(14, 30, 5), c(18, 0, 5)],
  },

  // ── SHOULDER (default / normal program) ──────────────────────────────────
  shoulder: {
    // 30 ON / 30 OFF, 6 AM–7 PM (13 runs) + three 15-min night pulses.
    nft: [
      ...genHourlyCycles(6, 19, 30, [0]),
      c(22, 0, 15), c(1, 30, 15), c(4, 30, 15),
    ],
    // 15-min runs on a ~45-min cadence through the day, easing off late.
    tower: [
      c(6, 0, 15), c(6, 45, 15), c(7, 30, 15), c(8, 15, 15),
      c(9, 0, 15), c(9, 45, 15), c(10, 15, 15), c(10, 45, 15),
      c(11, 15, 15), c(11, 45, 15), c(12, 15, 15), c(12, 45, 15),
      c(13, 15, 15), c(13, 45, 15), c(14, 15, 15), c(14, 45, 15),
      c(15, 15, 15), c(15, 45, 15), c(16, 30, 15), c(17, 15, 15),
      c(18, 0, 15),
      c(21, 15, 15), c(0, 30, 15), c(3, 45, 15),
    ],
    // 5-min pulses, every ~2.5 hours.
    nursery: [c(6, 0, 5), c(8, 30, 5), c(11, 0, 5), c(13, 30, 5), c(16, 0, 5), c(18, 30, 5)],
  },

  // ── HOT ──────────────────────────────────────────────────────────────────
  hot: {
    // 30 ON / 30 OFF, 6 AM–8 PM (14 runs) + four 15-min night pulses.
    nft: [
      ...genHourlyCycles(6, 20, 30, [0]),
      c(22, 0, 15), c(0, 30, 15), c(3, 0, 15), c(5, 30, 15),
    ],
    // 15 ON / 15 OFF, 6 AM–6:15 PM (25 runs) + five night pulses.
    tower: [
      ...genHourlyCycles(6, 18, 15, [0, 30]),
      c(18, 0, 15),
      c(20, 15, 15), c(22, 30, 15), c(0, 45, 15), c(3, 0, 15), c(5, 15, 15),
    ],
    // 5-min pulses, every ~2 hours + an evening top-up.
    nursery: [c(6, 0, 5), c(8, 0, 5), c(10, 0, 5), c(12, 0, 5), c(14, 0, 5), c(16, 0, 5), c(18, 0, 5), c(19, 30, 5)],
  },
};

/** Total ON minutes for a cycle list (the daily runtime). */
export function scheduleRuntimeMin(cycles) {
  return (cycles || []).reduce((sum, cy) => sum + (cy.on || 0), 0);
}
